import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Archive, ArchiveRestore } from "lucide-react";
import { api, apiError, money } from "@/lib/api";
import { Text, Area } from "@/components/admin/AdminUI";
import { MessageThread } from "@/components/MessageThread";

export function ArchiveControl({ request, onDone }) {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const archived = !!request.admin_archived;
  if (!archived && !request.hidden_by_customer) return null;

  const run = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/custom-requests/${request.id}/${archived ? "unarchive" : "archive"}`);
      toast.success(archived ? "Restored to your Custom Requests list" : "Moved to Archived");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (archived) return (
    <button data-testid="admin-unarchive-btn" onClick={run} disabled={busy}
      className="flex items-center gap-1.5 rounded-full border border-kys-blush-border px-3 py-1.5 text-xs font-medium text-kys-charcoal transition-colors hover:border-kys-ink">
      <ArchiveRestore className="h-3.5 w-3.5" /> Restore to my list
    </button>
  );

  // Inline confirm: a pop-up dialog here would open underneath the request window and freeze the page.
  if (!confirming) return (
    <button data-testid="admin-archive-btn" disabled={busy} onClick={() => setConfirming(true)}
      className="flex items-center gap-1.5 rounded-full border border-kys-blush-border px-3 py-1.5 text-xs font-medium text-kys-charcoal transition-colors hover:border-destructive hover:text-destructive">
      <Archive className="h-3.5 w-3.5" /> Remove from my list
    </button>
  );

  return (
    <div data-testid="admin-archive-dialog" className="w-full rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
      <p className="font-semibold">Remove {request.request_number} from your list?</p>
      <p className="mt-1">It moves to the Archived filter. Nothing is deleted — the request, messages, payment/order and fabric records are kept, and you can restore it anytime.</p>
      <div className="mt-2 flex gap-2">
        <button data-testid="admin-archive-cancel" onClick={() => setConfirming(false)} className="rounded-full border border-kys-blush-border bg-white px-3 py-1.5 font-medium">Keep it</button>
        <button data-testid="admin-archive-confirm" onClick={run} disabled={busy} className="rounded-full bg-destructive px-3 py-1.5 font-medium text-white disabled:opacity-60">{busy ? "Removing…" : "Remove"}</button>
      </div>
    </div>
  );
}

export function ApprovePanel({ request, onDone }) {
  const [f, setF] = useState({ final_price: request.final_price ?? request.quoted_price ?? "", ship_weight_oz: request.ship_weight_oz ?? "", message: "" });
  const [busy, setBusy] = useState(false);

  if (request.locked) return (
    <div data-testid="request-locked" className="rounded border border-green-200 bg-green-50 p-3 text-sm text-green-800">
      Paid &amp; locked — order <span className="font-mono">{request.paid_order_number}</span> · {money(request.final_price)}. Fulfil it from Orders.
    </div>
  );

  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/custom-requests/${request.id}/approve`, {
        final_price: Number(f.final_price), ship_weight_oz: Number(f.ship_weight_oz), message: f.message, origin: window.location.origin,
      });
      toast.success("Approved — customer can now purchase"); onDone(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="approve-panel" className="rounded border border-kys-accent/40 bg-kys-blush/50 p-4">
      <p className="text-sm font-semibold text-kys-ink">{request.status === "Approved" ? "Approved — update final price / weight" : "Approve for purchase"}</p>
      <p className="mb-3 text-xs text-kys-muted">Sets status to "Approved — Ready to Purchase", deducts held fabric, and messages the customer. Shipping + tax are added at checkout.</p>
      <div className="grid grid-cols-2 gap-3">
        <Text label="Final price (USD)" type="number" value={f.final_price} onChange={(v) => setF({ ...f, final_price: v })} testid="approve-price" />
        <Text label="Packed item weight (oz)" type="number" value={f.ship_weight_oz} onChange={(v) => setF({ ...f, ship_weight_oz: v })} hint="Box weight is added automatically" testid="approve-weight" />
      </div>
      <Area label="Message to customer (optional)" value={f.message} onChange={(v) => setF({ ...f, message: v })} rows={2} testid="approve-message" />
      <button data-testid="approve-btn" onClick={approve} disabled={busy || !f.final_price || !f.ship_weight_oz}
        className="mt-3 w-full rounded-full bg-kys-accent py-3 text-sm font-semibold text-white transition-colors hover:bg-kys-ink disabled:opacity-50">
        {busy ? "Approving…" : request.status === "Approved" ? "Update approval" : "Approve — Ready to Purchase"}
      </button>
    </div>
  );
}

export function AdminMessages({ request }) {
  const [messages, setMessages] = useState([]);
  const load = useCallback(() => api.get(`/admin/custom-requests/${request.id}/messages`).then((r) => setMessages(r.data)), [request.id]);
  useEffect(() => { load(); }, [load, request.status]);
  const send = async (body) => {
    await api.post(`/admin/custom-requests/${request.id}/messages`, { body, origin: window.location.origin });
    await load();
  };
  return (
    <div className="rounded border border-kys-blush-border p-4">
      <p className="text-sm font-semibold text-kys-ink">Messages with customer</p>
      <p className="mb-3 text-xs text-kys-muted">Visible to the customer in their account; they're emailed on each new message.</p>
      <MessageThread messages={messages} onSend={send} viewer="admin" />
    </div>
  );
}
